/**
 * UX19: Plant Definition fetch service
 * Fetches plant info JSON from Azure CDN, caches in memory.
 */

import { getCurrentLocale } from '@/i18n'
import { debug } from '@/utils/debug'
import type { PlantDefinition } from '@/types/plantDefinition'

const PLANT_DEFINITIONS_BASE_URL =
  (import.meta.env.VITE_PLANT_DEFINITIONS_URL as string | undefined)
  || 'https://zaprojektujogrodblob.blob.core.windows.net/plants/info'

/** In-memory cache: key = `{locale}/{code}` */
const cache = new Map<string, PlantDefinition | null>()

/** In-flight fetches to deduplicate concurrent requests */
const inflight = new Map<string, Promise<PlantDefinition | null>>()

/**
 * Fetch a plant definition by product code.
 * Returns cached result on subsequent calls. Returns null on 404.
 */
export async function getPlantDefinition(code: string): Promise<PlantDefinition | null> {
  const locale = getCurrentLocale()
  const cacheKey = `${locale}/${code}`

  if (cache.has(cacheKey)) {
    return cache.get(cacheKey)!
  }

  // Deduplicate concurrent requests
  const existing = inflight.get(cacheKey)
  if (existing) return existing

  const promise = fetchDefinition(cacheKey, locale, code)
  inflight.set(cacheKey, promise)

  try {
    return await promise
  } finally {
    inflight.delete(cacheKey)
  }
}

async function fetchDefinition(
  cacheKey: string,
  locale: string,
  code: string
): Promise<PlantDefinition | null> {
  const url = `${PLANT_DEFINITIONS_BASE_URL}/${locale}/${code}.json`
  debug.log('PlantInfo', `Fetching ${url}`)

  try {
    const response = await fetch(url)

    if (!response.ok) {
      if (response.status === 404) {
        debug.log('PlantInfo', `No definition found for ${code}`)
        cache.set(cacheKey, null)
        return null
      }
      throw new Error(`HTTP ${response.status}`)
    }

    const data: PlantDefinition = await response.json()
    cache.set(cacheKey, data)
    debug.log('PlantInfo', `Loaded ${code}: ${data.botanicalName}`)
    return data
  } catch (error) {
    debug.warn('PlantInfo', `Failed to fetch ${code}`, error)
    // Don't cache errors (allow retry)
    return null
  }
}
